'use client';

import {useState} from 'react';
import {Modal, ModalContent} from '@nextui-org/react';
import {cn} from '@features/ui/utils';
import {type FeedbackCategory} from '@features/db/feedbackAttachment';
import {Button} from '@features/ui/components/Button';
import CloseIcon from '@/icons/Close.svg';

const FEEDBACK_CATEGORIES: {value: FeedbackCategory; label: string}[] = [
  {value: 'layout', label: 'Layout'},
  {value: 'colors', label: 'Colors'},
  {value: 'typography', label: 'Typography'},
  {value: 'content', label: 'Content'},
  {value: 'spacing', label: 'Spacing'},
  {value: 'images', label: 'Images'},
];

type FeedbackModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {categories: FeedbackCategory[]; freeText: string}) => void;
};

export const FeedbackModal = ({
  isOpen,
  onClose,
  onSubmit,
}: FeedbackModalProps) => {
  const [selectedCategories, setSelectedCategories] = useState<
    FeedbackCategory[]
  >([]);
  const [freeText, setFreeText] = useState('');

  const handleCategoryToggle = (category: FeedbackCategory) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category],
    );
  };

  const handleSubmit = () => {
    onSubmit({
      categories: selectedCategories,
      freeText,
    });
    // Reset form after submission
    setSelectedCategories([]);
    setFreeText('');
  };

  const handleClose = () => {
    setSelectedCategories([]);
    setFreeText('');
    onClose();
  };

  const hasAnyInput =
    selectedCategories.length > 0 || freeText.trim().length > 0;

  return (
    <Modal
      backdrop='opaque'
      isOpen={isOpen}
      onClose={handleClose}
      className='w-[364px] bg-white text-black'
      hideCloseButton
      motionProps={{
        initial: {opacity: 0, scale: 0.9},
        animate: {opacity: 1, scale: 1},
        exit: {opacity: 0, scale: 0.9},
        transition: {duration: 0.15},
      }}
    >
      <ModalContent className='rounded-[18px] p-4'>
        <button
          onClick={handleClose}
          className='absolute right-4 top-[18px] rounded-sm opacity-70 transition-opacity hover:opacity-100'
        >
          <CloseIcon className='text-muted-foreground' />
        </button>
        <div className='mb-1 text-sm font-semibold'>
          What didn&apos;t you like?
        </div>
        <div className='mb-3 text-sm font-normal text-muted-foreground'>
          Tell us what you didn&apos;t like and we&apos;ll try fix those issues
        </div>

        {/* Category checkboxes */}
        <div className='mb-4 flex flex-wrap gap-1.5'>
          {FEEDBACK_CATEGORIES.map(({value, label}) => {
            const isSelected = selectedCategories.includes(value);
            return (
              <button
                key={value}
                onClick={() => handleCategoryToggle(value)}
                className={cn(
                  'rounded-full px-3 py-1 text-sm font-semibold',
                  isSelected
                    ? 'bg-primary text-white'
                    : 'bg-primary/10 text-primary hover:bg-primary/20',
                )}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Free text input */}
        <textarea
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          placeholder='Tell us more (optional)...'
          className='mb-4 w-full resize-none rounded-lg bg-muted p-2 text-sm text-black placeholder:text-muted-foreground focus:bg-muted'
          rows={3}
        />

        {/* Submit button */}
        <Button
          onClick={handleSubmit}
          disabled={!hasAnyInput}
          variant='primary'
          size='sm'
          className='flex w-full items-center justify-center'
        >
          Regenerate
        </Button>
      </ModalContent>
    </Modal>
  );
};
